/**
 * OutcomeProgress - Curriculum outcome list view
 *
 * Features:
 * - Group by subject (Math, ELA, Science, Social Studies)
 * - Table: Outcome | Students Assessed | Mastery Rate | Avg Score
 * - Click to see per-student breakdown
 */

import { useState, useMemo } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Target, Users, BookOpen, ChevronRight, ChevronDown } from "lucide-react";
import { MasteryBadge, getMasteryTextColor } from "./MasteryBadge";
import type { OutcomeProgress as OutcomeProgressType, OutcomeDetail, OutcomeStudentBreakdown } from "@/services/progress.service";
import { cn } from "@/lib/utils";

// =============================================================================
// Types
// =============================================================================

export interface OutcomeProgressProps {
  /** Outcome progress data */
  items: OutcomeProgressType[];
  /** Whether data is loading */
  loading: boolean;
  /** Outcome detail data for expanded view */
  expandedOutcomeDetail: OutcomeDetail | null;
  /** Loading state for outcome detail */
  detailLoading: boolean;
  /** Currently expanded outcome ID */
  expandedOutcomeId: string | null;
  /** Called when an outcome row is clicked */
  onOutcomeClick: (outcomeId: string) => void;
  /** Called to close the detail view */
  onDetailClose: () => void;
  /** Student name lookup function */
  getStudentName: (sheetNumber: string) => { name: string; isUnknown: boolean };
  /** Additional class names */
  className?: string;
}

// =============================================================================
// Subject Icons/Colors
// =============================================================================

const SUBJECT_CONFIG: Record<string, { color: string; bgColor: string }> = {
  'Math': { color: 'text-blue-600', bgColor: 'bg-blue-50 dark:bg-blue-950' },
  'Mathematics': { color: 'text-blue-600', bgColor: 'bg-blue-50 dark:bg-blue-950' },
  'ELA': { color: 'text-purple-600', bgColor: 'bg-purple-50 dark:bg-purple-950' },
  'English Language Arts': { color: 'text-purple-600', bgColor: 'bg-purple-50 dark:bg-purple-950' },
  'Science': { color: 'text-green-600', bgColor: 'bg-green-50 dark:bg-green-950' },
  'Social Studies': { color: 'text-amber-600', bgColor: 'bg-amber-50 dark:bg-amber-950' },
};

function getSubjectConfig(subject: string) {
  return SUBJECT_CONFIG[subject] || { color: 'text-slate-600', bgColor: 'bg-slate-50 dark:bg-slate-950' };
}

// =============================================================================
// Skeleton Loading
// =============================================================================

function OutcomeProgressSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="border rounded-lg p-4">
          <Skeleton className="h-6 w-32 mb-4" />
          <div className="space-y-3">
            {[1, 2].map((j) => (
              <div key={j} className="flex items-center gap-4">
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-6 w-20" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Empty State
// =============================================================================

function EmptyState() {
  return (
    <div className="py-12 text-center">
      <Target className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
      <p className="text-muted-foreground font-medium">No outcome data</p>
      <p className="text-sm text-muted-foreground mt-1">
        Scan worksheets linked to curriculum outcomes
      </p>
    </div>
  );
}

// =============================================================================
// Outcome Row Component
// =============================================================================

interface OutcomeRowProps {
  outcome: OutcomeProgressType;
  onClick: () => void;
}

function OutcomeRow({ outcome, onClick }: OutcomeRowProps) {
  const masteryRate = outcome.masteryRate !== null ? Math.round(outcome.masteryRate) : null;
  const avgPercentage = outcome.avgPercentage !== null ? Math.round(outcome.avgPercentage) : null;

  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      {/* Outcome Text */}
      <TableCell className="max-w-xs">
        <div className="min-w-0">
          <p className="font-medium text-sm line-clamp-2">{outcome.outcomeText}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {outcome.grade}
          </p>
        </div>
      </TableCell>

      {/* Students Assessed (hidden on mobile) */}
      <TableCell className="hidden sm:table-cell text-center">
        <div className="flex items-center justify-center gap-1">
          <Users className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-medium">{outcome.studentsAssessed}</span>
        </div>
      </TableCell>

      {/* Mastery Rate (hidden on small mobile) */}
      <TableCell className="hidden md:table-cell">
        {masteryRate !== null ? (
          <div className="flex items-center gap-2">
            <Progress
              value={masteryRate}
              className="h-2 w-16"
              style={{
                '--progress-indicator-color': masteryRate >= 80
                  ? 'rgb(34 197 94)'
                  : masteryRate >= 50
                  ? 'rgb(234 179 8)'
                  : 'rgb(239 68 68)',
              } as React.CSSProperties}
            />
            <span className="text-sm font-medium">{masteryRate}%</span>
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">--</span>
        )}
      </TableCell>

      {/* Avg Score & Status */}
      <TableCell>
        <div className="flex items-center justify-end gap-2">
          {avgPercentage !== null ? (
            <>
              <span className={cn('font-medium hidden sm:inline', getMasteryTextColor(avgPercentage))}>
                {avgPercentage}%
              </span>
              <MasteryBadge percentage={avgPercentage} size="sm" />
            </>
          ) : (
            <Badge variant="secondary" className="text-xs">No data</Badge>
          )}
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </TableCell>
    </TableRow>
  );
}

// =============================================================================
// Student Breakdown in Detail View
// =============================================================================

interface StudentBreakdownRowProps {
  student: OutcomeStudentBreakdown;
  studentName: string;
  isUnknown: boolean;
}

function StudentBreakdownRow({ student, studentName, isUnknown }: StudentBreakdownRowProps) {
  return (
    <div className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50">
      <div
        className={cn(
          'h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0',
          isUnknown ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'
        )}
      >
        {studentName.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn('text-sm font-medium truncate', isUnknown && 'text-muted-foreground')}>
          {studentName}
        </p>
        <p className="text-xs text-muted-foreground">
          {student.assessments} assessment{student.assessments !== 1 ? 's' : ''}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <span className={cn('font-medium', getMasteryTextColor(student.avgPercentage))}>
          {Math.round(student.avgPercentage)}%
        </span>
        <MasteryBadge percentage={student.avgPercentage} size="sm" />
      </div>
    </div>
  );
}

// =============================================================================
// Outcome Detail Panel
// =============================================================================

interface OutcomeDetailPanelProps {
  detail: OutcomeDetail;
  getStudentName: (sheetNumber: string) => { name: string; isUnknown: boolean };
}

function OutcomeDetailPanel({ detail, getStudentName }: OutcomeDetailPanelProps) {
  const subjectConfig = getSubjectConfig(detail.subject);

  // Calculate stats
  const avgPercentage = useMemo(() => {
    if (detail.students.length === 0) return null;
    const total = detail.students.reduce((sum, s) => sum + s.avgPercentage, 0);
    return Math.round(total / detail.students.length);
  }, [detail.students]);

  const masteryCount = useMemo(() => {
    return detail.students.filter((s) => s.avgPercentage >= 80).length;
  }, [detail.students]);

  return (
    <div className="space-y-6 mt-4">
      {/* Outcome Header */}
      <div className={cn('p-4 rounded-lg', subjectConfig.bgColor)}>
        <Badge variant="outline" className={cn('mb-2', subjectConfig.color)}>
          {detail.subject} - {detail.grade}
        </Badge>
        <p className="font-medium">{detail.outcomeText}</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-3 rounded-lg bg-muted/50">
          <p className="text-2xl font-bold">{detail.students.length}</p>
          <p className="text-xs text-muted-foreground">Students</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-muted/50">
          <p className={cn('text-2xl font-bold', avgPercentage !== null && getMasteryTextColor(avgPercentage))}>
            {avgPercentage !== null ? `${avgPercentage}%` : '--'}
          </p>
          <p className="text-xs text-muted-foreground">Avg Score</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-muted/50">
          <p className="text-2xl font-bold text-green-600">{masteryCount}</p>
          <p className="text-xs text-muted-foreground">Mastered</p>
        </div>
      </div>

      {/* Student List */}
      <div>
        <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          Student Performance
        </h3>
        {detail.students.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <p className="text-sm">No student data</p>
          </div>
        ) : (
          <ScrollArea className="h-[300px] pr-3">
            <div className="space-y-1">
              {detail.students
                .sort((a, b) => a.avgPercentage - b.avgPercentage) // Lowest first
                .map((student) => {
                  const { name, isUnknown } = getStudentName(student.sheetNumber);
                  return (
                    <StudentBreakdownRow
                      key={student.sheetNumber}
                      student={student}
                      studentName={name}
                      isUnknown={isUnknown}
                    />
                  );
                })}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Worksheets */}
      {detail.worksheets.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-muted-foreground" />
            Worksheets Testing This Outcome
          </h3>
          <div className="space-y-2">
            {detail.worksheets.map((ws) => (
              <div
                key={ws.worksheetId}
                className="flex items-center justify-between p-2 rounded-md bg-muted/50"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{ws.worksheetTitle}</p>
                  <p className="text-xs text-muted-foreground">{ws.scanCount} scans</p>
                </div>
                {ws.avgPercentage !== null && (
                  <Badge variant="secondary">{Math.round(ws.avgPercentage)}% avg</Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Subject Section Component
// =============================================================================

interface SubjectSectionProps {
  subject: string;
  outcomes: OutcomeProgressType[];
  onOutcomeClick: (outcomeId: string) => void;
  defaultOpen?: boolean;
}

function SubjectSection({ subject, outcomes, onOutcomeClick, defaultOpen = true }: SubjectSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const subjectConfig = getSubjectConfig(subject);
  const avgMastery = outcomes.reduce((sum, o) => sum + (o.masteryRate || 0), 0) / outcomes.length;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border rounded-lg overflow-hidden">
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className={cn('w-full justify-between px-4 py-3 h-auto', subjectConfig.bgColor)}
        >
          <div className="flex items-center gap-3 flex-1">
            <BookOpen className={cn('h-5 w-5', subjectConfig.color)} />
            <span className={cn('font-semibold', subjectConfig.color)}>{subject}</span>
            <Badge variant="secondary" className="ml-auto mr-2">
              {outcomes.length} outcome{outcomes.length !== 1 ? 's' : ''}
            </Badge>
            {avgMastery > 0 && (
              <span className="text-sm text-muted-foreground">
                {Math.round(avgMastery)}% mastery
              </span>
            )}
          </div>
          <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Outcome</TableHead>
              <TableHead className="hidden sm:table-cell text-center w-24">Students</TableHead>
              <TableHead className="hidden md:table-cell w-32">Mastery Rate</TableHead>
              <TableHead className="w-32 text-right">Avg Score</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {outcomes.map((outcome) => (
              <OutcomeRow
                key={outcome.outcomeId}
                outcome={outcome}
                onClick={() => onOutcomeClick(outcome.outcomeId)}
              />
            ))}
          </TableBody>
        </Table>
      </CollapsibleContent>
    </Collapsible>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function OutcomeProgress({
  items,
  loading,
  expandedOutcomeDetail,
  detailLoading,
  expandedOutcomeId,
  onOutcomeClick,
  onDetailClose,
  getStudentName,
  className,
}: OutcomeProgressProps) {
  // Group items by subject
  const groupedBySubject = useMemo(() => {
    const groups: Record<string, OutcomeProgressType[]> = {};

    for (const item of items) {
      const subject = item.subject || 'Other';
      if (!groups[subject]) {
        groups[subject] = [];
      }
      groups[subject].push(item);
    }

    // Sort outcomes within each group by mastery rate (lowest first)
    for (const subject of Object.keys(groups)) {
      groups[subject].sort((a, b) => {
        const aRate = a.masteryRate ?? 0;
        const bRate = b.masteryRate ?? 0;
        return aRate - bRate;
      });
    }

    return groups;
  }, [items]);

  const subjects = useMemo(() => Object.keys(groupedBySubject).sort(), [groupedBySubject]);

  // Find expanded outcome for title
  const expandedOutcome = useMemo(() => {
    if (!expandedOutcomeId) return null;
    return items.find((o) => o.outcomeId === expandedOutcomeId) || null;
  }, [items, expandedOutcomeId]);

  if (loading) {
    return <OutcomeProgressSkeleton />;
  }

  if (items.length === 0) {
    return <EmptyState />;
  }

  return (
    <>
      <div className={cn('space-y-3', className)}>
        {subjects.map((subject) => (
          <SubjectSection
            key={subject}
            subject={subject}
            outcomes={groupedBySubject[subject]}
            onOutcomeClick={onOutcomeClick}
          />
        ))}
      </div>

      {/* Outcome Detail Dialog */}
      <Dialog open={!!expandedOutcomeId} onOpenChange={(open: boolean) => !open && onDetailClose()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Outcome Details</DialogTitle>
            <DialogDescription>
              {expandedOutcome?.subject} - {expandedOutcome?.grade}
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="space-y-4 mt-6">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : expandedOutcomeDetail ? (
            <OutcomeDetailPanel
              detail={expandedOutcomeDetail}
              getStudentName={getStudentName}
            />
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <p>Unable to load outcome details</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default OutcomeProgress;
